// A2F-764: swatch-palette color picker.
//
// Faster than the OS color input for marketing-page UX:
//   - Six curated brand-aligned swatches (or eight for table palettes)
//     scan in milliseconds.
//   - A trailing "Custom" swatch opens the native picker so any value
//     the schema accepts is still reachable; a custom-picked value
//     fills the trailing swatch (no gradient overlay) so the user can
//     see what they chose.
//
// The schema is unchanged — `value` and `onChange` traffic in the
// same hex strings the OS picker emitted before.

import { useRef } from 'react'

interface ColorSwatchProps {
  value: string
  palette: readonly string[]
  onChange: (hex: string) => void
  /** A11y label prefix — e.g. "Heading color". */
  ariaLabelPrefix: string
}

const RAINBOW =
  'conic-gradient(from 0deg, #f43f5e, #f59e0b, #84cc16, #06b6d4, #6366f1, #ec4899, #f43f5e)'

export function ColorSwatch({
  value,
  palette,
  onChange,
  ariaLabelPrefix,
}: ColorSwatchProps) {
  const customRef = useRef<HTMLInputElement>(null)
  const valueLower = value.toLowerCase()
  const inPalette = palette.some((p) => p.toLowerCase() === valueLower)

  return (
    <div className="flex flex-wrap items-center gap-2">
      {palette.map((color) => {
        const selected = !inPalette
          ? false
          : color.toLowerCase() === valueLower
        return (
          <button
            key={color}
            type="button"
            onClick={() => onChange(color)}
            aria-label={`${ariaLabelPrefix} ${color}`}
            aria-pressed={selected}
            className={`h-9 w-9 rounded-md transition-shadow ${
              selected
                ? 'ring-2 ring-brand-orange ring-offset-2 ring-offset-white'
                : 'ring-1 ring-slate-200 hover:ring-slate-300'
            }`}
            style={{ background: color }}
          />
        )
      })}

      {/* Custom escape hatch. When the current value isn't in the
          palette, this swatch fills with that value (and is the
          "selected" one). Otherwise it shows the rainbow gradient
          to advertise the affordance. */}
      <button
        type="button"
        onClick={() => customRef.current?.click()}
        aria-label={`${ariaLabelPrefix} custom color`}
        aria-pressed={!inPalette}
        title={inPalette ? 'Pick a custom color' : `Custom: ${value}`}
        className={`relative h-9 w-9 rounded-md transition-shadow overflow-hidden ${
          !inPalette
            ? 'ring-2 ring-brand-orange ring-offset-2 ring-offset-white'
            : 'ring-1 ring-slate-200 hover:ring-slate-300'
        }`}
        style={{
          background: !inPalette ? value : RAINBOW,
        }}
      >
        {inPalette ? (
          <span
            aria-hidden
            className="absolute inset-0 flex items-center justify-center text-[8px] font-bold uppercase tracking-wider text-white"
            style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}
          >
            Custom
          </span>
        ) : null}
        <input
          ref={customRef}
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="sr-only"
          aria-label={`${ariaLabelPrefix} custom color value`}
        />
      </button>
    </div>
  )
}

// Curated palettes — exported so table-style editor reuses the same
// discipline. The hex values matter: the default value of every field
// the palette covers MUST appear in the palette, otherwise the picker
// renders the "Custom" swatch as selected on first paint.
//
// TEXT_COLOR_PALETTE.[1] === BASE_FORMATTING.headings.colors.1 / .2.
// TABLE_COLOR_PALETTE.[0] === DEFAULT_TABLE_STYLES[0].headerBg.
export const TEXT_COLOR_PALETTE = [
  '#221610', // Charcoal — Chrona brand
  '#2F5496', // Word blue — matches BASE_FORMATTING.headings.colors.1
  '#EC5B13', // Chrona orange
  '#475569', // Slate
  '#000000', // Black
] as const

export const TABLE_COLOR_PALETTE = [
  '#E7E6E6', // Light gray — matches DEFAULT_TABLE_STYLES seed headerBg
  '#221610', // Charcoal
  '#2F5496', // Word blue
  '#EC5B13', // Chrona orange
  '#475569', // Slate
  '#000000', // Black
] as const
